/**
 * Usage meters this product can enforce.
 *
 * Keys must match `server/agent/services/subscription/meters.py` exactly -- the same string
 * identifies the meter on the Utilities plan. Labels and order come from here; whether a meter
 * is enforced comes from the server, so defaults live in one place only.
 */

export const METER_AI_CREDITS = 'ai-credits';
export const METER_AI_KNOWLEDGE_FOLDERS = 'ai-knowledge-folders';
export const METER_AI_LIBRARY_STORAGE = 'ai-library-storage';

export type MeterUnit = 'credits' | 'count' | 'bytes';

export interface UsageMeterDefinition {
  meterKey: string;
  /** Translation key. */
  label: string;
  /** Translation key. */
  description: string;
  unit: MeterUnit;
}

export const USAGE_METER_CATALOG: readonly UsageMeterDefinition[] = [
  {
    meterKey: METER_AI_CREDITS,
    label: 'USAGE_CONFIG_METER_AI_CREDITS_LABEL',
    description: 'USAGE_CONFIG_METER_AI_CREDITS_DESCRIPTION',
    unit: 'credits',
  },
  {
    meterKey: METER_AI_KNOWLEDGE_FOLDERS,
    label: 'USAGE_CONFIG_METER_KNOWLEDGE_FOLDERS_LABEL',
    description: 'USAGE_CONFIG_METER_KNOWLEDGE_FOLDERS_DESCRIPTION',
    unit: 'count',
  },
  {
    meterKey: METER_AI_LIBRARY_STORAGE,
    label: 'USAGE_CONFIG_METER_LIBRARY_STORAGE_LABEL',
    description: 'USAGE_CONFIG_METER_LIBRARY_STORAGE_DESCRIPTION',
    unit: 'bytes',
  },
] as const;

/** UILM translation keys, not text: render every value through `t()`. */
export const USAGE_LIMITS_I18N = {
  TITLE: 'USAGE_CONFIG',
  DESCRIPTION: 'USAGE_CONFIG_DESCRIPTION',
  ENFORCEMENT_LABEL: 'USAGE_CONFIG_ENFORCEMENT_LABEL',
  ENFORCED: 'USAGE_CONFIG_ENFORCED',
  NOT_ENFORCED: 'USAGE_CONFIG_NOT_ENFORCED',
  ENFORCEMENT_OFF_NOTE: 'USAGE_CONFIG_ENFORCEMENT_OFF_NOTE',
  METER_KEY_COLUMN: 'USAGE_CONFIG_METER_KEY_COLUMN',
  /** The unit an operator must use for includedQuantity on the plan. */
  UNIT_LABELS: {
    credits: 'CREDITS_UNIT',
    count: 'USAGE_CONFIG_UNIT_FOLDERS',
    bytes: 'USAGE_CONFIG_UNIT_BYTES',
  } as Record<MeterUnit, string>,
  UNIT_HINT: 'USAGE_CONFIG_UNIT_HINT',
  METER_KEY_HINT: 'USAGE_CONFIG_METER_KEY_HINT',
  WHAT_AGENTS_CHECK: 'USAGE_CONFIG_WHAT_AGENTS_CHECK',
  SETUP_HINT: 'USAGE_CONFIG_SETUP_HINT',
  NO_LIMIT_SKIP_CHECK: 'USAGE_CONFIG_NO_LIMIT_SKIP_CHECK',
  LOAD_FAILED: 'USAGE_CONFIG_LOAD_FAILED',
  DEFAULTS_NOTE: 'USAGE_CONFIG_DEFAULTS_NOTE',
  DEFAULTS_WARNING_TITLE: 'USAGE_CONFIG_DEFAULTS_WARNING_TITLE',
  DEFAULTS_WARNING_BODY: 'USAGE_CONFIG_DEFAULTS_WARNING_BODY',
  OPEN_USAGE_CONFIG: 'USAGE_CONFIG_OPEN_USAGE_CONFIG',
  SAVE: 'SAVE',
  DISCARD: 'DISCARD',
  SAVED: 'USAGE_CONFIG_SAVED',
  SAVE_FAILED: 'USAGE_CONFIG_SAVE_FAILED',

  TAB_TENANT: 'USAGE_CONFIG_TAB_TENANT',
  TAB_ORGANIZATIONS: 'USAGE_CONFIG_TAB_ORGANIZATIONS',
  PAGE_SUBTITLE: 'USAGE_CONFIG_PAGE_SUBTITLE',

  ORGANIZATION_SEARCH: 'SEARCH_ORGANIZATIONS',
  SHOWING_COUNT: 'SHOWING_X_OF_Y',
  NOTHING_FOUND: 'NOTHING_FOUND',
  SELECT_TO_CONFIGURE: 'USAGE_CONFIG_SELECT_TO_CONFIGURE',

  CHIP_CUSTOM: 'CUSTOM',
  CHIP_INHERITED_TENANT: 'USAGE_CONFIG_CHIP_INHERITED_TENANT',
  ORGANIZATION_DESCRIPTION: 'USAGE_CONFIG_ORGANIZATION_DESCRIPTION',
  ORGANIZATION_SETUP_HINT: 'USAGE_CONFIG_ORGANIZATION_SETUP_HINT',
  ACTIVE_LABEL: 'USAGE_CONFIG_ACTIVE_LABEL',
  BLOCKED: 'USAGE_CONFIG_BLOCKED',
  NOT_BLOCKED: 'ACTIVE',
  BLOCKED_NOTE: 'USAGE_CONFIG_BLOCKED_NOTE',

  INHERITED_FROM_TENANT: 'USAGE_CONFIG_INHERITED_FROM_TENANT',
  OVERRIDDEN_HERE: 'USAGE_CONFIG_OVERRIDDEN_HERE',
  FOLLOW_TENANT: 'USAGE_CONFIG_FOLLOW_TENANT',
  TENANT_ONLY_NOTE: 'USAGE_CONFIG_TENANT_ONLY_NOTE',
  ORGANIZATION_INHERIT_NOTE: 'USAGE_CONFIG_ORGANIZATION_INHERIT_NOTE',

  SETTLEMENT_TITLE: 'USAGE_CONFIG_SETTLEMENT_TITLE',
  SETTLEMENT_DESCRIPTION: 'USAGE_CONFIG_SETTLEMENT_DESCRIPTION',
  SETTLEMENT_ORG: 'USAGE_CONFIG_SETTLEMENT_ORG',
  SETTLEMENT_ORG_NOTE: 'USAGE_CONFIG_SETTLEMENT_ORG_NOTE',
  SETTLEMENT_USER: 'USAGE_CONFIG_SETTLEMENT_USER',
  SETTLEMENT_USER_NOTE: 'USAGE_CONFIG_SETTLEMENT_USER_NOTE',

  LIMIT_WINDOWS_TITLE: 'USAGE_CONFIG_LIMIT_WINDOWS_TITLE',
  LIMIT_WINDOWS_DESCRIPTION: 'USAGE_CONFIG_LIMIT_WINDOWS_DESCRIPTION',
  WINDOW_NAME_LABEL: 'NAME',
  WINDOW_TYPE_LABEL: 'TYPE',
  WINDOW_RENAME: 'USAGE_CONFIG_WINDOW_RENAME',
  WINDOW_TYPE_HOURLY: 'HOURLY',
  WINDOW_TYPE_DAILY: 'DAILY',
  WINDOW_ADD: 'USAGE_CONFIG_WINDOW_ADD',
  WINDOW_DEFAULT_NAME: 'USAGE_CONFIG_WINDOW_DEFAULT_NAME',
  WINDOW_TYPE_OF: 'USAGE_CONFIG_WINDOW_TYPE_OF',
  WINDOW_REMOVE: 'CREATE_SKILL_REMOVE_TOOL',
  WINDOW_ENABLED: 'USAGE_CONFIG_WINDOW_ENABLED',
  WINDOW_ON: 'USAGE_CONFIG_WINDOW_ON',
  WINDOW_OFF: 'USAGE_CONFIG_WINDOW_OFF',
  WINDOW_EMPTY: 'USAGE_CONFIG_WINDOW_EMPTY',
  WINDOW_OFF_NOTE: 'USAGE_CONFIG_WINDOW_OFF_NOTE',
  WINDOW_HOURS: 'USAGE_CONFIG_WINDOW_HOURS',
  WINDOW_HOURS_SUFFIX: 'USAGE_CONFIG_WINDOW_HOURS_SUFFIX',
  WINDOW_WORKING_DAYS: 'USAGE_CONFIG_WINDOW_WORKING_DAYS',
  WINDOW_DAYS_SUFFIX: 'USAGE_CONFIG_WINDOW_DAYS_SUFFIX',
  WINDOW_SPAN: 'USAGE_CONFIG_WINDOW_SPAN',
  WINDOW_BURST: 'USAGE_CONFIG_WINDOW_BURST',
  WINDOW_BURST_SUFFIX: 'USAGE_CONFIG_WINDOW_BURST_SUFFIX',
  WINDOW_KEY: 'USAGE_CONFIG_ENTITLEMENT_KEY',
  WINDOW_KEY_HINT: 'USAGE_CONFIG_WINDOW_KEY_HINT',
  WINDOW_KEY_COPY: 'USAGE_CONFIG_WINDOW_KEY_COPY',
  METER_KEY_COPY: 'USAGE_CONFIG_METER_KEY_COPY',
  KEY_COPIED: 'COPIED',
  KEY_EMPTY: 'USAGE_CONFIG_KEY_EMPTY',
  KEY_TAKEN: 'USAGE_CONFIG_KEY_TAKEN',
  KEY_BLOCKS_SAVE: 'USAGE_CONFIG_KEY_BLOCKS_SAVE',
  WINDOW_FORMULA_HOURLY: 'USAGE_CONFIG_WINDOW_FORMULA_HOURLY',
  WINDOW_FORMULA_DAILY: 'USAGE_CONFIG_WINDOW_FORMULA_DAILY',
  WINDOW_CLAMPED: 'USAGE_CONFIG_WINDOW_CLAMPED',
  WINDOW_LENGTH_HOURLY: 'USAGE_CONFIG_WINDOW_LENGTH_HOURLY',
  WINDOW_LENGTH_ONE_DAY: 'USAGE_CONFIG_WINDOW_LENGTH_ONE_DAY',
  WINDOW_LENGTH_DAILY: 'USAGE_CONFIG_WINDOW_LENGTH_DAILY',
  NO_WINDOW_LIMIT: 'USAGE_CONFIG_NO_WINDOW_LIMIT',
  WINDOW_SHARE_NOTE: 'USAGE_CONFIG_WINDOW_SHARE_NOTE',
  WINDOW_SHARE_EMPTY: 'USAGE_CONFIG_WINDOW_SHARE_EMPTY',
  WINDOW_SHARE_PERCENT: 'USAGE_CONFIG_WINDOW_SHARE_PERCENT',
  WINDOW_SHARE_SUFFIX: 'USAGE_CONFIG_WINDOW_SHARE_SUFFIX',
  WINDOW_SHARE_OF: 'USAGE_CONFIG_WINDOW_SHARE_OF',
  WINDOW_LIMITS: 'USAGE_CONFIG_WINDOW_LIMITS',
  WINDOW_SHARE_TITLE: 'USAGE_CONFIG_WINDOW_SHARE_TITLE',
  WINDOW_SHARE_DONE: 'DONE',
  WINDOW_SHARE_EDIT_FOR: 'USAGE_CONFIG_WINDOW_SHARE_EDIT_FOR',
  WINDOW_SHARE_NONE_SET: 'USAGE_CONFIG_WINDOW_SHARE_NONE_SET',

  CATEGORIES_TITLE: 'USAGE_CONFIG_CATEGORIES_TITLE',
  CATEGORIES_DESCRIPTION: 'USAGE_CONFIG_CATEGORIES_DESCRIPTION',
  CATEGORIES_LABEL: 'USAGE_CONFIG_CATEGORIES_LABEL',
  CATEGORIES_OFF_NOTE: 'USAGE_CONFIG_CATEGORIES_OFF_NOTE',
  CATEGORIES_RESTRICTED_NOTE: 'USAGE_CONFIG_CATEGORIES_RESTRICTED_NOTE',
  CATEGORIES_EMPTY: 'USAGE_CONFIG_CATEGORIES_EMPTY',
  CATEGORY_ADD: 'USAGE_CONFIG_CATEGORY_ADD',
  CATEGORY_REMOVE: 'REMOVE',
  CATEGORY_NAME: 'NAME',
  CATEGORY_KEY: 'USAGE_CONFIG_ENTITLEMENT_KEY',
  CATEGORY_MODELS: 'MODELS',
  CATEGORY_PICK_MODELS: 'USAGE_CONFIG_CHOOSE_MODELS',
  CATEGORY_KEY_HINT: 'USAGE_CONFIG_CATEGORY_KEY_HINT',
  CATEGORY_KEY_PLACEHOLDER: 'USAGE_CONFIG_CATEGORY_KEY_PLACEHOLDER',
  CATEGORY_KEY_COPY: 'USAGE_CONFIG_CATEGORY_KEY_COPY',
  CATEGORY_DEFAULT_NAME: 'USAGE_CONFIG_CATEGORY_DEFAULT_NAME',
  CATEGORY_FILTER_MODELS: 'USAGE_CONFIG_CATEGORY_FILTER_MODELS',
  CATEGORY_MODELS_TITLE: 'USAGE_CONFIG_CATEGORY_MODELS_TITLE',
  CATEGORY_SELECTED_COUNT: 'SELECTED_COUNT',
  CATEGORY_ALSO_IN: 'USAGE_CONFIG_CATEGORY_ALSO_IN',
  CATEGORY_MORE: 'USAGE_CONFIG_CATEGORY_MORE',
  CATEGORY_EDIT_MODELS: 'USAGE_CONFIG_CATEGORY_EDIT_MODELS',
  MODELS_GROUP_BLOCKS_OF: 'USAGE_CONFIG_MODELS_GROUP_BLOCKS_OF',
  MODEL_TYPE_LABELS: {
    chat: 'USAGE_CONFIG_MODEL_TYPE_CHAT',
    embedding: 'USAGE_CONFIG_MODEL_TYPE_EMBEDDING',
    image: 'USAGE_CONFIG_MODEL_TYPE_IMAGE',
    audio: 'USAGE_CONFIG_MODEL_TYPE_AUDIO',
    tts: 'USAGE_CONFIG_MODEL_TYPE_TTS',
    rerank: 'USAGE_CONFIG_MODEL_TYPE_RERANK',
  } as Record<string, string>,
  MODELS_GROUP_CUSTOM: 'USAGE_CONFIG_MODELS_GROUP_CUSTOM',
  MODELS_CUSTOM_EMPTY: 'USAGE_CONFIG_MODELS_CUSTOM_EMPTY',
  APPLY: 'APPLY',
  CANCEL: 'CANCEL',

  GRANTS_NOTE: 'USAGE_CONFIG_GRANTS_NOTE',
  GRANTS_RULE: 'USAGE_CONFIG_GRANTS_RULE',

  MODEL_RESTRICTION_TITLE: 'USAGE_CONFIG_MODEL_RESTRICTION_TITLE',
  MODEL_RESTRICTION_DESCRIPTION: 'USAGE_CONFIG_MODEL_RESTRICTION_DESCRIPTION',
  MODEL_RESTRICTION_ORGANIZATION_DESCRIPTION: 'USAGE_CONFIG_MODEL_RESTRICTION_ORGANIZATION_DESCRIPTION',
  MODEL_RESTRICTION_LABEL: 'USAGE_CONFIG_MODEL_RESTRICTION_LABEL',
  MODEL_RESTRICTION_OFF_NOTE: 'USAGE_CONFIG_MODEL_RESTRICTION_OFF_NOTE',
  MODEL_RESTRICTION_ON_NOTE: 'USAGE_CONFIG_MODEL_RESTRICTION_ON_NOTE',
  MODEL_RESTRICTION_EMPTY: 'USAGE_CONFIG_MODEL_RESTRICTION_EMPTY',
  MODEL_RESTRICTION_PLAN_NOTE: 'USAGE_CONFIG_MODEL_RESTRICTION_PLAN_NOTE',
  MODEL_RESTRICTION_PICK: 'USAGE_CONFIG_CHOOSE_MODELS',
  MODEL_RESTRICTION_EDIT: 'USAGE_CONFIG_MODEL_RESTRICTION_EDIT',
  MODEL_RESTRICTION_COUNT_ONE: 'USAGE_CONFIG_MODEL_RESTRICTION_COUNT_ONE',
  MODEL_RESTRICTION_COUNT_OTHER: 'USAGE_CONFIG_MODEL_RESTRICTION_COUNT_OTHER',
  MODEL_PICKER_TITLE: 'USAGE_CONFIG_MODEL_PICKER_TITLE',
  MODEL_RESTRICTION_CUSTOM_EMPTY: 'USAGE_CONFIG_MODEL_RESTRICTION_CUSTOM_EMPTY',
} as const;

/**
 * The organization a caller falls into when their token carries no organization claim
 * (the server's DEFAULT_ORG). IAM never lists it, so the views add it themselves.
 * `name` is a translation key.
 */
export const DEFAULT_ORGANIZATION = {
  itemId: 'default',
  name: 'USAGE_CONFIG_DEFAULT_ORGANIZATION',
} as const;
